import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  prospectDetails: {
    title: '',
    forename: '',
    surname: '',
    dateOfBirth: '',
    retrieveQuotedateOfBirth: '',
    gender: '',
    emailAddress: '',
    telephoneNumber: '',
    coverStartDate: '',
    address: {},
    bankDetails: {},
    dependents: {},
    marketingOptOut: {},
    showPreferredPaymentDaySelector: false,
  },
  callbackDetails: {
    forename: '',
    surname: '',
    contactNumber: '',
  },
  productCode: '',
  selectedQuote: null,
  toCoverText: '',
  declarations: [],
};

const QuoteDataSlice = createSlice({
  name: 'quoteData',
  initialState,
  reducers: {
    setProspectDetails(state, action) {
      state.prospectDetails = action.payload;
    },
    setOtherDefaults: state => {
      if (!state.prospectDetails.address) {
        state.prospectDetails.address = {};
      }
      state.prospectDetails.bankDetails = {};
      state.prospectDetails.marketingOptOut = {};
      state.productCode = '';
      state.selectedQuote = null;
      state.toCoverText = '';
      state.declarations = [];
    },

    setEmptyDependents: state => {
      state.prospectDetails.dependents = {};
    },

    setSelectedChildIndex(state, action) {
      state.selectedChildIndex = action.payload;
    },

    updateProspectTitle: (state, action) => {
      state.prospectDetails.title = action.payload;
    },
    updateProspectForename: (state, action) => {
      state.prospectDetails.forename = action.payload;
      state.callbackDetails.forename = action.payload;
    },
    updateProspectSurname: (state, action) => {
      state.prospectDetails.surname = action.payload;
      state.callbackDetails.surname = action.payload;
    },
    updateCallbackForename: (state, action) => {
      state.callbackDetails.forename = action.payload;
    },
    updateCallbackSurname: (state, action) => {
      state.callbackDetails.surname = action.payload;
    },
    updateCallbackContactNumber: (state, action) => {
      state.callbackDetails.contactNumber = action.payload;
    },
    updateProspectDateOfBirth: (state, action) => {
      state.prospectDetails.dateOfBirth = action.payload.split('T')[0];
    },
    updateRetrieveQuoteProspectDateOfBirth: (state, action) => {
      state.prospectDetails.retrieveQuotedateOfBirth = action.payload.split('T')[0];
    },
    updateProspectGender: (state, action) => {
      state.prospectDetails.gender = action.payload;
    },
    updateProspectPostcode: (state, action) => {
      state.prospectDetails.address = { postCode: action.payload };
    },
    updateProspectCoverStartDate: (state, action) => {
      state.prospectDetails.coverStartDate = action.payload;
    },
    updateProspectAddress: (state, action) => {
      state.prospectDetails.address = { ...state.prospectDetails.address, ...action.payload };
    },
    updateProspectAddressManualLine1: (state, action) => {
      state.prospectDetails.address.line1 = action.payload;
    },
    updateProspectAddressManualLine2: (state, action) => {
      state.prospectDetails.address.line2 = action.payload;
    },
    updateProspectAddressManualLine3: (state, action) => {
      state.prospectDetails.address.line3 = action.payload;
    },
    updateAccountHolderName: (state, action) => {
      state.prospectDetails.bankDetails.accountHolder = action.payload;
    },
    updateSortCode: (state, action) => {
      state.prospectDetails.bankDetails.branchSortCode = action.payload;
    },
    updateAccountNumber: (state, action) => {
      state.prospectDetails.bankDetails.accountNumber = action.payload;
    },
    updateBankName: (state, action) => {
      state.prospectDetails.bankDetails.bankName = action.payload;
    },
    updateBankAddress: (state, action) => {
      state.prospectDetails.bankDetails.bankAddress = action.payload;
    },
    updateIsAccountHolder: (state, action) => {
      state.prospectDetails.bankDetails.isAccountHolder = action.payload;
    },
    updateIsOnlySignatory: (state, action) => {
      state.prospectDetails.bankDetails.isOnlySignatory = action.payload;
    },
    updateEmailPreference: (state, action) => {
      state.prospectDetails.marketingOptOut.email = action.payload;
    },
    updateSMSTextPreference: (state, action) => {
      state.prospectDetails.marketingOptOut.smsText = action.payload;
    },
    updatePartnerTitle: (state, action) => {
      if (state.prospectDetails.dependents.spouse !== undefined) {
        state.prospectDetails.dependents.spouse.title = action.payload;
      } else {
        state.prospectDetails.dependents.spouse = { title: action.payload };
      }
    },
    updatePartnerFirstname: (state, action) => {
      if (state.prospectDetails.dependents.spouse !== undefined) {
        state.prospectDetails.dependents.spouse.forename = action.payload;
      } else {
        state.prospectDetails.dependents.spouse = { forename: action.payload };
      }
    },
    updatePartnerLastname: (state, action) => {
      if (state.prospectDetails.dependents.spouse !== undefined) {
        state.prospectDetails.dependents.spouse.surname = action.payload;
      } else {
        state.prospectDetails.dependents.spouse = { surname: action.payload };
      }
    },
    updatePartnerDetails: (state, action) => {
      state.prospectDetails.dependents.spouse = { dateOfBirth: action.payload.split('T')[0] };
    },
    updatePartnerGender: (state, action) => {
      if (state.prospectDetails.dependents.spouse !== undefined) {
        state.prospectDetails.dependents.spouse.gender = action.payload;
      } else {
        state.prospectDetails.dependents.spouse = { gender: action.payload };
      }
    },
    updateChildTitle: (state, action) => {
      if (action.payload.index !== undefined) {
        state.prospectDetails.dependents.children[action.payload.index].title =
          action.payload.value;
      } else {
        state.prospectDetails.dependents.children[0].title = action.payload;
      }
    },
    updateChildFirstname: (state, action) => {
      state.prospectDetails.dependents.children[0].forename = action.payload;
    },
    updateChildLastname: (state, action) => {
      state.prospectDetails.dependents.children[0].surname = action.payload;
    },
    updateChildrenFirstname: (state, action) => {
      if (action.payload.index !== undefined) {
        state.prospectDetails.dependents.children[action.payload.index].forename =
          action.payload.value;
      }
    },
    updateChildrenLastname: (state, action) => {
      if (action.payload.index !== undefined) {
        state.prospectDetails.dependents.children[action.payload.index].surname =
          action.payload.value;
      }
    },
    updateChildDetails: (state, action) => {
      if (action.payload.index !== undefined) {
        state.prospectDetails.dependents.children[action.payload.index].dateOfBirth =
          action.payload.value?.split('T')[0];
      } else {
        state.prospectDetails.dependents.children[0].dateOfBirth = action.payload.split('T')[0];
      }
    },
    updateChildGender: (state, action) => {
      if (action.payload.index !== undefined) {
        state.prospectDetails.dependents.children[action.payload.index].gender =
          action.payload.value;
      } else {
        state.prospectDetails.dependents.children[0].gender = action.payload;
      }
    },
    updateNumberOfChildren: (state, action) => {
      const children = new Array(action.payload).fill({});
      state.prospectDetails.dependents.children = children.map((c, i) => {
        return state.prospectDetails.dependents.children
          ? state.prospectDetails.dependents.children[i] || {}
          : c;
      });
    },
    updateProspectEmail: (state, action) => {
      state.prospectDetails.emailAddress = action.payload;
    },
    updateProspectContactNumber: (state, action) => {
      state.prospectDetails.telephoneNumber = action.payload;
      state.callbackDetails.contactNumber = action.payload;
    },
    updateProductCode: (state, action) => {
      state.productCode = action.payload;
    },
    updateSelectedQuote: (state, action) => {
      state.selectedQuote = action.payload;
    },
    updateToCoverText: (state, action) => {
      state.toCoverText = action.payload;
    },
    setDeclaration: (state, action) => {
      state.declarations = action.payload;
    },
    updatePaymentDay: (state, action) => {
      state.prospectDetails.bankDetails.paymentDay = action.payload;
    },
    updateShowPreferredPaymentDaySelector: (state, action) => {
      state.prospectDetails.showPreferredPaymentDaySelector = action.payload;
    },

    resetSpouse: state => {
      delete state.prospectDetails.dependents.spouse;
    },
    resetChildren: state => {
      delete state.prospectDetails.dependents.children;
    },
    resetQuoteData: () => initialState,
  },
});

export const {
  updateProductCode,
  updateSelectedQuote,
  updateToCoverText,
  resetSpouse,
  resetChildren,
  resetQuoteData,
  setProspectDetails,
  setOtherDefaults,
  setEmptyDependents,
  setSelectedChildIndex,
  updateBankName,
  updateBankAddress,
  setDeclaration,
  updateShowPreferredPaymentDaySelector,
  updatePaymentDay,
} = QuoteDataSlice.actions;
export default QuoteDataSlice.reducer;
